import { useEffect, useRef } from 'react';
import { Icon } from '@/features/common/Icon';
import { useDismiss } from '@/features/common/useDismiss';
import { useListboxKeys } from '@/features/common/useListboxKeys';
import '@/features/common/dropdown.css';
import type { DropdownProps } from '@/features/common/types/DropdownProps';

/**
 * Custom listbox replacing a native <select> — a native popup can't be styled (border/
 * shadow/selected-state), so this is a real listbox built from scratch, shared by every page
 * that needs one instead of each screen hand-rolling its own. NewsAdminPage's status/category
 * toolbar filters (single-select, size="sm") and CabinetPage's territorial-units field
 * (multi-select, size="md") both render through this one component.
 *
 * `active` is the caller's to set, never worked out here from the value: which value counts as
 * "narrowing" belongs to the page — a filter off "all" is one, a form field with an answer is not
 * — and deciding it here would restyle every dropdown on the site at once.
 */
export function Dropdown(props: DropdownProps) {
  const {
    id,
    ariaLabel,
    placeholder,
    options,
    size,
    disabled,
    active,
    invalid,
    describedBy,
    isOpen,
    onToggle,
    onClose,
  } = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useDismiss(rootRef, isOpen, onClose);

  // An opened list is brought into view. Inside a scrolling container — a modal's fields — the
  // list opens under its trigger and past the container's edge, where the options it just offered
  // cannot be seen; a list already in view does not move.
  useEffect(() => {
    if (isOpen) menuRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen]);

  const isSelected = (value: string) =>
    props.multiple ? props.value.includes(value) : props.value === value;
  const hasSelection = props.multiple ? props.value.length > 0 : !!props.value;
  const triggerText = props.multiple
    ? props.value.length > 0
      ? props.summaryLabel(props.value.length)
      : placeholder
    : (options.find((o) => o.value === props.value)?.label ?? placeholder);

  const selectedIndex = options.findIndex((o) => isSelected(o.value));
  const keys = useListboxKeys(
    isOpen,
    options.map((o) => o.label),
    selectedIndex,
    triggerRef,
    onToggle,
    onClose,
  );

  const pick = (value: string) => {
    if (props.multiple) {
      const set = new Set(props.value);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      props.onChange([...set]);
      // Multi-select stays open — picking a second/third unit shouldn't require reopening.
    } else {
      props.onChange(value);
      onClose();
      // The chosen option unmounts with the list; the focus goes back to what opened it.
      triggerRef.current?.focus();
    }
  };

  return (
    <div
      className={`dropdown-select dropdown-select--${size}${active ? ' dropdown-select--active' : ''}`}
      ref={rootRef}
    >
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`dropdown-select__trigger${isOpen ? ' dropdown-select__trigger--open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={onToggle}
        onKeyDown={keys.onTriggerKeyDown}
      >
        <span className={hasSelection ? undefined : 'dropdown-select__placeholder'}>
          {triggerText}
        </span>
        <Icon name="chevron-bottom" className="dropdown-select__chevron" />
      </button>
      {isOpen && !disabled && (
        // Named like its trigger, and the <li> wrappers presentational: a listbox owns options, not
        // list items, and axe reported every open list on the site as an unnamed listbox of
        // orphaned options (aria-input-field-name, aria-required-children/-parent, listitem).
        <ul
          ref={menuRef}
          className="dropdown-select__menu"
          role="listbox"
          aria-label={ariaLabel}
          aria-multiselectable={props.multiple === true}
          onKeyDown={keys.onListKeyDown}
        >
          {options.map((o, index) => {
            const selected = isSelected(o.value);
            return (
              <li key={o.value} role="presentation">
                <button
                  ref={(el) => {
                    keys.optionRefs.current[index] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`dropdown-select__option${selected ? ' dropdown-select__option--selected' : ''}`}
                  onClick={() => pick(o.value)}
                >
                  <span>{o.label}</span>
                  {selected && <Icon name="checkmark-small" className="dropdown-select__check" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
